import { makeStyles } from "@fluentui/react-components";
import { designTokens } from "theme/customTheme";

const TAB_CONTENT_HEIGHT = "560px";

export const useStyles = makeStyles({
  dialogContent: {
    height: TAB_CONTENT_HEIGHT,
    maxHeight: "78vh",
    position: "relative",
  },
  scrollableContent: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto" as const,
  },
  charCount: {
    fontSize: designTokens.fontSize.xs,
    color: designTokens.neutral[400],
    textAlign: "right",
    marginTop: designTokens.spacing[1],
  },
  capabilityGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: designTokens.spacing[2],
  },
  capabilitySkeletonCard: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[2],
    padding: `${designTokens.spacing[2]} ${designTokens.spacing[3]}`,
    borderRadius: designTokens.borderRadius.md,
    border: `1px solid ${designTokens.neutral[200]}`,
    backgroundColor: designTokens.neutral.white,
    height: "60px",
  },
  capabilitySkeletonMeta: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[1],
    flex: 1,
  },
  capabilityCard: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[2],
    padding: `${designTokens.spacing[2]} ${designTokens.spacing[3]}`,
    borderRadius: designTokens.borderRadius.md,
    border: `1px solid ${designTokens.neutral[200]}`,
    backgroundColor: designTokens.neutral.white,
    cursor: "pointer",
    transition: `all ${designTokens.transition.fast}`,
    userSelect: "none" as const,
    minWidth: 0,
    minHeight: "60px",
    ":hover": {
      border: `1px solid ${designTokens.neutral[300]}`,
      backgroundColor: designTokens.neutral[50],
    },
    ":focus-visible": {
      outlineStyle: "solid",
      outlineWidth: "2px",
      outlineColor: designTokens.accent[500],
      outlineOffset: "2px",
    },
  },
  capabilityCardEnabled: {
    border: `1px solid ${designTokens.accent[300]}`,
    backgroundColor: designTokens.accent[50],
    ":hover": {
      border: `1px solid ${designTokens.accent[400]}`,
      backgroundColor: designTokens.accent[100],
    },
  },
  capabilityIconWrap: {
    width: "28px",
    height: "28px",
    borderRadius: designTokens.borderRadius.sm,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    backgroundColor: designTokens.accent[100],
    color: designTokens.accent[600],
    "& svg": {
      width: "14px",
      height: "14px",
    },
  },
  capabilityMeta: {
    flex: 1,
    minWidth: 0,
  },
  capabilityNameRow: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[1],
    flexWrap: "wrap" as const,
  },
  capabilityName: {
    fontSize: designTokens.fontSize.xs,
    fontWeight: designTokens.fontWeight.semibold,
    color: designTokens.neutral[700],
    lineHeight: "1.3",
  },
  capabilityDesc: {
    fontSize: "10px",
    color: designTokens.neutral[400],
    marginTop: "1px",
    lineHeight: "1.3",
    overflow: "hidden",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical" as const,
  },
  addCapCard: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: designTokens.spacing[1],
    padding: `${designTokens.spacing[3]} ${designTokens.spacing[2]}`,
    borderRadius: designTokens.borderRadius.md,
    border: `1.5px dashed ${designTokens.accent[400]}`,
    backgroundColor: "transparent",
    cursor: "pointer",
    gridColumn: "1 / -1",
    color: designTokens.accent[600],
    fontWeight: designTokens.fontWeight.semibold,
    fontSize: designTokens.fontSize.xs,
    transition: `all ${designTokens.transition.fast}`,
    userSelect: "none" as const,
    ":hover": {
      backgroundColor: designTokens.accent[50],
      border: `1.5px dashed ${designTokens.accent[500]}`,
    },
    ":focus-visible": {
      outlineStyle: "solid",
      outlineWidth: "2px",
      outlineColor: designTokens.accent[500],
      outlineOffset: "2px",
    },
  },
  addCapCardExpanded: {
    gridColumn: "1 / -1",
    border: `1.5px dashed ${designTokens.accent[400]}`,
    borderRadius: designTokens.borderRadius.md,
    padding: designTokens.spacing[3],
    backgroundColor: designTokens.accent[50],
    display: "flex",
    flexDirection: "column" as const,
    gap: designTokens.spacing[2],
  },
  customCapBadge: {
    fontSize: "9px",
    fontWeight: designTokens.fontWeight.semibold,
    color: designTokens.accent[600],
    backgroundColor: designTokens.accent[100],
    borderRadius: designTokens.borderRadius.sm,
    padding: "1px 5px",
    textTransform: "uppercase" as const,
    letterSpacing: "0.5px",
    lineHeight: "1.4",
    flexShrink: 0,
  },
  customCapEditCard: {
    gridColumn: "1 / -1",
    border: `1px solid ${designTokens.accent[300]}`,
    backgroundColor: designTokens.accent[50],
    borderRadius: designTokens.borderRadius.md,
    padding: designTokens.spacing[3],
    display: "flex",
    flexDirection: "column" as const,
    gap: designTokens.spacing[2],
  },
  inlineFormRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: designTokens.spacing[2],
    alignItems: "flex-start",
  },
  inlineFormActions: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: designTokens.spacing[1],
  },
  inlineFormButtons: {
    display: "flex",
    gap: designTokens.spacing[2],
  },
  capabilityCardSwitch: {
    marginRight: `-${designTokens.spacing[2]}`,
  },
  capabilityCardActions: {
    display: "flex",
    gap: "4px",
    alignItems: "center",
    flexShrink: 0,
    marginLeft: "auto",
  },
  capabilityCardDeleteConfirm: {
    border: `1px solid ${designTokens.accent[300]}`,
    backgroundColor: designTokens.accent[50],
    ":hover": {
      border: `1px solid ${designTokens.accent[300]}`,
      backgroundColor: designTokens.accent[50],
    },
  },
  capabilityCardDisabled: {
    opacity: "0.55",
  },
  disabledBadge: {
    fontSize: "9px",
    fontWeight: designTokens.fontWeight.semibold,
    color: designTokens.neutral[500],
    backgroundColor: designTokens.neutral[100],
    borderRadius: designTokens.borderRadius.sm,
    padding: "1px 5px",
    textTransform: "uppercase" as const,
    letterSpacing: "0.5px",
    lineHeight: "1.4",
    flexShrink: 0,
  },
  inlineDeleteMessage: {
    fontSize: designTokens.fontSize.xs,
    color: designTokens.neutral[700],
    margin: 0,
  },
  inlineDeleteUrl: {
    fontSize: "10px",
    color: designTokens.neutral[400],
    margin: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as const,
    maxWidth: "100%",
    cursor: "default",
  },
  errorMessage: {
    color: designTokens.semantic.error,
    marginTop: designTokens.spacing[2],
    fontSize: designTokens.fontSize.sm,
  },
  brandingTabLayout: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[4],
  },
  brandingFormColumn: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[4],
  },
  brandingPreviewColumn: {
    display: "flex",
    flexDirection: "column",
  },
  previewLabel: {
    display: "block",
    color: designTokens.neutral[500],
    marginBottom: designTokens.spacing[2],
    textTransform: "uppercase" as const,
    letterSpacing: "0.05em",
  },
  logoPreviewWrapper: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[3],
    marginTop: designTokens.spacing[1],
  },
  logoPreview: {
    maxHeight: "48px",
    maxWidth: "200px",
    objectFit: "contain" as const,
    border: `1px solid ${designTokens.neutral[200]}`,
    borderRadius: designTokens.borderRadius.md,
    padding: designTokens.spacing[1],
  },
  colorPickerRow: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[3],
    marginTop: designTokens.spacing[1],
  },
  colorInput: {
    width: "48px",
    height: "36px",
    padding: designTokens.spacing[1],
    border: `1px solid ${designTokens.neutral[300]}`,
    borderRadius: designTokens.borderRadius.md,
    cursor: "pointer",
    backgroundColor: "transparent",
  },
  hexInput: {
    width: "110px",
    fontFamily: "'Consolas', 'Courier New', monospace",
  },
});
